import { computed } from 'mobx'
import { task } from 'mobx-task'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ActivationLifecycle } from '@/infra/activation'
import { type TermsAcceptancesListViewModel } from '@/features/account/terms/TermsAcceptancesList/TermsAcceptancesListViewModel'
import { type Section7216AcceptancesListViewModel } from '@/features/account/section-7216/Section7216AcceptancesList/Section7216AcceptancesListViewModel'
import { type WorkspaceMemberStore } from '@/features/iam/members/WorkspaceMemberStore'
import {
  type GetCurrentMemberFn,
  type GetCurrentUserFn,
  type GetCurrentWorkspaceFn,
} from '@/features/iam'
import { type SnackbarFactory } from '@/features/shell'
import { firstValueFrom } from 'rxjs'
import { toStream } from '@/utils/mobx-util'
import { toAbortSignal } from '@/utils/rx-util'
import { createAbortError, resolveOnAbort } from '@/utils/abort-util'

/**
 * View model for managing personal info.
 */
export class PersonalInfoViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  constructor(
    private readonly _getCurrentWorkspace: GetCurrentWorkspaceFn,
    private readonly _getCurrentUser: GetCurrentUserFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _memberStore: WorkspaceMemberStore,
    private readonly _newSnackbar: SnackbarFactory,
    public readonly termsAcceptancesListVm: TermsAcceptancesListViewModel,
    public readonly section7216ListVm: Section7216AcceptancesListViewModel,
  ) {
    this._lifecycle.manage(termsAcceptancesListVm)
    this._lifecycle.manage(section7216ListVm)
  }

  @computed
  get workspace() {
    return this._getCurrentWorkspace()
  }

  @computed
  get user() {
    return this._getCurrentUser()
  }

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Changes the avatar.
   */
  changeAvatar = task.resolved(async (file: File) => {
    const snackbar = this._newSnackbar((s) =>
      s.saying('Uploading', 'Please wait...').type('pending').show(),
    )

    using dismissed = toAbortSignal(
      snackbar.dismissed$,
      createAbortError('User canceled upload'),
    )

    // Set up a promise that resolves once the avatar has been changed.
    // This ensures we don't consider the action done until the user
    // has been reloaded, which avoids the avatar flickering as the image
    // reloads.
    const reflected = firstValueFrom(
      toStream(() => this._getCurrentUser().avatarUrl),
    )

    await this._memberStore
      .changeAvatar(this._getCurrentMember(), file, dismissed.signal)
      .then(() =>
        snackbar
          .type('success')
          .saying('Success!', 'Your profile picture has been updated')
          .autoDismiss(),
      )
      .catch((e) => {
        snackbar.fromError(e)
        throw e
      })

    // Don't wait forever on this.
    await resolveOnAbort(
      reflected,
      AbortSignal.any([dismissed.signal, AbortSignal.timeout(5_000)]),
    )
  })
}
